import { useState, useEffect, useMemo } from "react";
import { Table, TableBody, TableBodyRow, TableHeaderItemProps } from "components/table";
import { Input } from "components";
import { SearchIconTwo } from "assets";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useFetchUsageBreakdown, ActivityFilter, useExportUsageBreakdown } from "hooks";
import { PaginationTwo } from "components";

const PAGE_SIZE = 8;

interface UsageRecord {
  id: string;
  reference: string;
  date: string;
  activity: string;
  credit: number;
  balance: number;
}

const mockData: UsageRecord[] = [
  { id: "1", reference: "123455", date: "September 1, 2025", activity: "CV Analysis", credit: -5, balance: 2000 },
  { id: "2", reference: "123455", date: "September 1, 2025", activity: "Report Generated", credit: -10, balance: 1932 },
  { id: "3", reference: "123455", date: "September 1, 2025", activity: "Candidate Screening", credit: -20, balance: 1955 },
  { id: "4", reference: "123455", date: "September 1, 2025", activity: "CV Analysis", credit: -30, balance: 1955 },
  { id: "5", reference: "123455", date: "September 1, 2025", activity: "CV Analysis", credit: -10, balance: 1955 },
  { id: "6", reference: "123455", date: "September 1, 2025", activity: "Report Generated", credit: -3, balance: 1955 },
];

const searchSchema = Yup.object().shape({
  search: Yup.string().trim(),
});

const UsageBreakdownTable = () => {
  const [selectedActivity, setSelectedActivity] = useState("All Activities");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [isActivityDropdownOpen, setIsActivityDropdownOpen] = useState(false);
  const [enableExport, setEnableExport] = useState(false);

  const { register, formState: { errors }, watch } = useForm({
    resolver: yupResolver(searchSchema),
    mode: "onBlur",
    defaultValues: {
      search: "",
    },
  });
  const watchSearch = watch("search");

  const users = ["All users", "John Doe", "Jane Smith", "Mike Johnson"];
  const activities = [
    { label: "All Activities", value: undefined },
    { label: "CV Analysis", value: "cv_analysis" as ActivityFilter },
    { label: "Generate Questions", value: "generate_questions" as ActivityFilter },
    { label: "Refine Question", value: "refine_question" as ActivityFilter },
    { label: "Assessment Scoring", value: "assessment_scoring" as ActivityFilter },
    { label: "CV Relevance", value: "cv_relevance" as ActivityFilter },
    { label: "Strengths & Gaps", value: "strengths_gaps" as ActivityFilter },
    { label: "Comparison", value: "comparison" as ActivityFilter },
    { label: "Share Report", value: "share_report" as ActivityFilter },
    { label: "Analytics", value: "analytics" as ActivityFilter },
    { label: "Collaborative Session", value: "collaborative_session" as ActivityFilter },
    { label: "JD Upload", value: "jd_upload" as ActivityFilter },
    { label: "JD Generate", value: "jd_generate" as ActivityFilter },
  ];

  // Fetch usage breakdown data
  const selectedActivityValue = activities.find(a => a.label === selectedActivity)?.value;
  const { data: usageData, isLoading } = useFetchUsageBreakdown({
    activity: selectedActivityValue,
    search: searchQuery,
    page: currentPage,
  });

  const totalCount = usageData?.count || 0;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  // Export hook
  const { data: exportData, isLoading: isExporting } = useExportUsageBreakdown(enableExport, {
    activity: selectedActivityValue,
    search: searchQuery,
  }, selectedRows);

  useEffect(() => {
    if (exportData && !isExporting) {
      // Download CSV
      const blob = new Blob([exportData], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `usage-breakdown-${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setEnableExport(false);
    }
  }, [exportData, isExporting]);
  const tableData: UsageRecord[] = useMemo(() => {
    if (!usageData?.results) return [];

    return usageData.results.map((record) => ({
      id: record.id.toString(),
      reference: record.reference,
      date: record.date,
      activity: record.activity,
      credit: record.credit,
      balance: record.balance,
    }));
  }, [usageData]);

  // Watch for search input changes
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(watchSearch?.trim() || "");
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [watchSearch]);

  const toggleRowSelection = (id: string) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
    );
  };

  const toggleAllRows = () => {
    if (selectedRows.length === filteredData.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(filteredData.map((row) => row.id));
    }
  };

  const filteredData = tableData;

  // Custom checkbox header element
  const CheckboxHeader = (
    <input
      type="checkbox"
      checked={selectedRows.length === filteredData.length && filteredData.length > 0}
      onChange={toggleAllRows}
      className="w-5 h-5 rounded-md border-gray-300 cursor-pointer accent-[#012169]"
    />
  );

  const tableHeaders: TableHeaderItemProps[] = [
    { title: CheckboxHeader, styles: { width: "50px", flexShrink: 0 } },
    { title: "ID", styles: { flex: 1 } },
    { title: "Date", styles: { flex: 1 } },
    { title: "Activity", styles: { flex: 1 } },
    { title: "Credit", styles: { flex: 1 } },
    { title: "Balance", styles: { flex: 1 } },
  ];

  return (
    <div>
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between mb-12">
        <div className="flex flex-wrap gap-3 items-center">
          {/* User Filter */}
          {/* <div className="relative">
            <button
              onClick={() => {
                setIsUserDropdownOpen(!isUserDropdownOpen);
                setIsActivityDropdownOpen(false);
              }}
              className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:border-gray-300"
            >
              {selectedUser}
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {isUserDropdownOpen && (
              <div className="absolute left-0 mt-1 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                {users.map((user) => (
                  <button
                    key={user}
                    onClick={() => {
                      setSelectedUser(user);
                      setIsUserDropdownOpen(false);
                    }}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg ${
                      selectedUser === user ? "text-[#012169] font-medium" : "text-gray-600"
                    }`}
                  >
                    {user}
                  </button>
                ))}
              </div>
            )}
          </div> */}

          {/* Activity Filter */}
          <div className="relative">
            <button
              onClick={() => {
                setIsActivityDropdownOpen(!isActivityDropdownOpen);
                setIsUserDropdownOpen(false);
              }}
              className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:border-gray-300"
            >
              {selectedActivity}
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {isActivityDropdownOpen && (
              <div className="absolute left-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                {activities.map((activity) => (
                  <button
                    key={activity.label}
                    onClick={() => {
                      setSelectedActivity(activity.label);
                      setCurrentPage(1);
                      setIsActivityDropdownOpen(false);
                    }}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg ${
                      selectedActivity === activity.label ? "text-[#012169] font-medium" : "text-gray-600"
                    }`}
                  >
                    {activity.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Search */}
          <Input
            Icon={SearchIconTwo}
            iconClassName="!top-[11px] !left-[14px]"
            placeholder="Search here"
            type="search"
            name="search"
            register={register}
            validatorMessage={errors.search?.message || ""}
            showRequired={false}
            parentClassName="!mb-0 !w-fit"
            className="!h-[42px] !rounded-lg !border-gray-200 hover:!border-gray-300 !pl-10 !text-sm !text-gray-600 placeholder:!text-gray-400 !w-fit min-w-[200px]"
          />
        </div>

        {/* Export Button */}
        <button 
          onClick={() => setEnableExport(true)}
          disabled={isExporting || selectedRows.length === 0}
          className="flex items-center justify-center gap-2 px-5 py-2.5 bg-[#012169] text-white text-sm font-medium rounded-lg hover:bg-[#011557] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isExporting ? "Exporting..." : "Export CSV"}
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
          </svg>
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <Table
          tableHeaderTitles={tableHeaders}
          customTableClasses={{
            tableContainerClassName: "min-w-[700px]",
            tableHeaderClassName: "bg-transparent border-b border-gray-100 !shadow-none !justify-start !gap-0",
            tableHeaderItemClassName: "!text-[#1D2939] text-sm font-medium !p-0",
          }}
          tableBody={
            <TableBody customClassName="mt-0">
              {filteredData.map((record) => (
                <TableBodyRow
                  key={record.id}
                  customClassName="!shadow-none border-b border-gray-50 hover:bg-gray-50 !rounded-none !mb-0 !justify-start !gap-0"
                >
                  <span style={{ width: "50px", flexShrink: 0 }} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedRows.includes(record.id)}
                      onChange={() => toggleRowSelection(record.id)}
                      className="w-5 h-5 rounded-md border-gray-300 cursor-pointer accent-[#012169]"
                    />
                  </span>
                  <span style={{ flex: 1 }} className="text-sm text-gray-600">{record.id}</span>
                  <span style={{ flex: 1 }} className="text-sm text-gray-600">{record.date}</span>
                  <span style={{ flex: 1 }} className="text-sm font-medium text-gray-900">{record.activity}</span>
                  <span style={{ flex: 1 }} className="text-sm font-medium text-gray-900">{record.credit}</span>
                  <span style={{ flex: 1 }} className="text-sm text-[#012169]">{record.balance}</span>
                </TableBodyRow>
              ))}
            </TableBody>
          }
          emptyTable={{
            show: isLoading || filteredData.length === 0,
            element: isLoading ? (
              <div className="text-center py-10">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#012169] mx-auto mb-4"></div>
                <p className="text-gray-600">Loading usage data...</p>
              </div>
            ) : (
              <div className="text-center py-10 text-gray-500">
                No usage records found
              </div>
            ),
          }}
        />
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <PaginationTwo
          current={currentPage}
          total={totalPages}
          handleChange={setCurrentPage}
          count={totalCount}
          length={PAGE_SIZE}
        />
      )}
    </div>
  );
};

export { UsageBreakdownTable };
